import Cookies from "js-cookie";
import { toast } from "react-toastify";
import i18n from "../i18n";
import { isTokenExpired, refreshAccessToken } from "../services/authService";
import {
    getToken,
    handleApiSuccess,
    userFriendlyMessage,
} from "../utils/tokenUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Request interceptors
const applyRequestInterceptors = (config) => {
  // Add common headers, auth tokens, etc.
  let token = getToken();

  if (config.requiresAuth && token && isTokenExpired()) {
    throw { isTokenRefreshRequired: true };
  }

  const isFormData = config.body instanceof FormData;
  const headers = {
    ...(config.requiresAuth && token
      ? { Authorization: `Bearer ${token}` }
      : {}),
    ...config.headers,
  };

  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  return {
    ...config,
    headers,
    body: isFormData
      ? config.body
      : config.body
      ? JSON.stringify(config.body)
      : null,
  };
};

// Response interceptors
const applyResponseInterceptors = async (response, config) => {
  if (response.status === 401) {
    if (config.requiresAuth && !config._retry) {
      throw { isTokenRefreshRequired: true };
    }
    if (config.requiresAuth) {
      throw { isUnauthorized: true };
    }
    return response;
  }

  if (response.status === 400) {
    const errorData = await parseErrorResponse(response);
    throw errorData;
  }

  if (!response.ok) {
    const errorData = await parseErrorResponse(response);
    throw errorData;
  }

  return parseSuccessResponse(
    response,
    config.method,
    config.endpoint,
    config?.suppressToast
  );
};

// Helper functions
const parseErrorResponse = async (response) => {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : { message: "Request failed" };
  } catch {
    return { message: text || "Request failed" };
  }
};

const parseSuccessResponse = async (
  response,
  method,
  endpoint,
  suppressToast
) => {
  const text = await response.text();

  if (!suppressToast) {
      handleApiSuccess(method, endpoint);
  }

  try {
    const data = text ? JSON.parse(text) : {};
    if (typeof data === "string" && data.trim()) {
      if (!suppressToast) {
          toast.success(data);
      }
      return { message: data };
    }

    // Handle messageKey-based i18n responses (e.g. from DELETE endpoints)
    if (!suppressToast && data.messageKey) {
      const translated = i18n.t(data.messageKey, { defaultValue: data.message });
      toast.success(translated);
    }

    return data;
  } catch {
    if (text.trim()) {
      if (!suppressToast) {
          toast.success(text.trim());
      }
      return { message: text.trim() };
    }
    return {};
  }
};

// Main apiClient function
const apiClient = async (
  endpoint,
  {
    method = "GET",
    body = null,
    headers = {},
    requiresAuth = true,
    suppressToast = false,
  } = {}
) => {
  const config = {
    method,
    body,
    headers,
    requiresAuth,
    endpoint,
    suppressToast,
  };

  try {
    // Apply request interceptors
    const processedConfig = applyRequestInterceptors(config);

    // Make the request
    const response = await fetch(`${API_BASE_URL}${endpoint}`, processedConfig);

    // Apply response interceptors
    return await applyResponseInterceptors(response, config);
  } catch (error) {
    // Handle special error cases
    if (error.isTokenRefreshRequired) {
      try {
        const token = await refreshAccessToken();
        if (!token) throw { isUnauthorized: true };

        // Retry with new token
        const retryConfig = {
          ...config,
          headers: { ...config.headers, Authorization: `Bearer ${token}` },
          _retry: true,
        };
        const response = await fetch(`${API_BASE_URL}${endpoint}`, retryConfig);
        return await applyResponseInterceptors(response, retryConfig);
      } catch (refreshError) {
        handleUnauthorized();
        throw new Error("Session expired. Please log in again.");
      }
    }

    if (error.isUnauthorized) {
      handleUnauthorized();
      throw new Error("Unauthorized - Redirecting to Login");
    }

    // Log and display the error (use messageKey for i18n if available)
    const displayMessage = error.messageKey
      ? i18n.t(error.messageKey, { defaultValue: error.message })
      : error.message?.trim()?.length < 100
        ? error.message?.trim()
        : userFriendlyMessage(error);
    !suppressToast && toast.error(displayMessage);

    throw error;
  }
};

const handleUnauthorized = () => {
  Cookies.remove("accessTokenJKT");
  Cookies.remove("refreshTokenJKT");
  window.location.href = "/";
};

export default apiClient;
